import { CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { Permission } from '@procureai/permissions';
import { DateFormatPreset } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SvbrgContractItem } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import {
  cellFactory,
  CurrencyCell,
  CuriosityLinkCell,
  DataGridConfig,
  dateFormatterFactory,
  GridAlignmentType,
  gridColDefFactory,
  UseDataGridConfig,
} from '~/components/DataGridCustom';
import { RouteId } from '~/routes/RouteId';

export enum ContractItemSearchDataGridField {
  ID = 'id',
  TITLE = 'title',
  CREATED_AT = 'createdAtTimestamp',
  PLANNED_QUANTITY_FLOAT = 'plannedQuantityFloat',
  SUPPLIER = 'supplier',
  PRICE_PER_UNIT_FLOAT = 'pricePerUnitFloat',
}

export const useContractItemResultDataGrid: UseDataGridConfig<SvbrgContractItem> = () => {
  const { t } = useTranslation();
  const { t: tColumn } = useTranslation(I18nNamespace.intake, {
    keyPrefix: 'ContractItemResultDataGrid.column',
  });

  return useMemo(() => {
    const createColumn = gridColDefFactory({
      tColumn,
      t,
      defaultOptions: { sortable: false, align: GridAlignmentType.LEFT, minWidth: 120 },
    });
    return {
      sortModel: [],
      columnVisibilityModel: {},
      columns: [
        createColumn({
          field: ContractItemSearchDataGridField.ID,
          renderCell: cellFactory(CuriosityLinkCell, ({ row }) => ({
            ns: CuriosityNamespaceType.CONTRACT,
            to: RouteId.contractDetail(row.contract.id),
            needs: { permission: Permission.BaseContractList },
          })),
          valueGetter: (_value, row) => row.contract.id,
          hideable: false,
        }),
        createColumn({
          field: ContractItemSearchDataGridField.TITLE,
          minWidth: 400,
        }),
        createColumn({
          field: ContractItemSearchDataGridField.CREATED_AT,
          valueGetter: (_value, row) => new Date(row.createdAtTimestamp * 1000),
          valueFormatter: dateFormatterFactory({ preset: DateFormatPreset.TWO_DIGIT }),
        }),
        createColumn({
          field: ContractItemSearchDataGridField.PLANNED_QUANTITY_FLOAT,
        }),
        createColumn({
          field: ContractItemSearchDataGridField.SUPPLIER,
          minWidth: 250,
          renderCell: cellFactory(CuriosityLinkCell, ({ row }) => ({
            ns: CuriosityNamespaceType.SUPPLIER,
            to: row.contract.supplierId ? RouteId.supplierDetail(row.contract.supplierId) : undefined,
            needs: { permission: Permission.BaseSupplierList },
          })),
          valueGetter: (_value, row) => row.contract.supplierId,
        }),
        createColumn({
          field: ContractItemSearchDataGridField.PRICE_PER_UNIT_FLOAT,
          align: GridAlignmentType.RIGHT,
          renderCell: cellFactory(CurrencyCell, ({ row }) => ({
            currency: row.contract.currency ?? undefined,
            value: row.pricePerUnitFloat,
          })),
        }),
      ],
    } as DataGridConfig<SvbrgContractItem>;
  }, [tColumn, t]);
};
